import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { Button, Divider, Grid, Icon, SectionHeader, Link } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import RouteKey from '../../../../routes/key';
import { candidateService } from '../../../../api';
import ApplicationStatus from '../summary/ApplicationStatus';
import PersonalDetails from '../summary/PersonalDetails';
import TransactionDetails from '../summary/TransactionDetails';
import FeeDetails from '../summary/FeeDetails';
import SelectedSubjectsReview from '../summary/SelectedSubjectsReview';
import { parseLocationSearch } from '../../../../utils/StringUtils';
import AppealOutcome from '../summary/AppealOutcome';
import { FileUploadState } from '../../../../models/File';
import DownloadProgressModal from '../../../../components/modals/DownloadProgressModal';

const { appeal } = RouteKey.child;
const { applications } = RouteKey.appeal.child;

const useStyles = makeStyles()((theme) =>
({
  title: {
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(7),
    marginRight: theme.spacing(2),
  },
  downloadPdf: {
    display: 'flex',
    paddingLeft: theme.spacing(3),
  },
  downloadPdfText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
  },
}));

const SectionHeaderAdditionalComponent = (
  downloadAppealSummary: () => void,
  isFetchingDownloadAppealSummary: boolean,
  theme: AugmentedTheme,
  classes: Record<string,string>
) =>
  isFetchingDownloadAppealSummary ? (
    <Icon name={IconName.loadingIndicator} size="L" color={theme.color.link.linkActive02} />
  ) : (
    <Link
      className={classes.downloadPdf}
      id="link-download"
      onClick={() => downloadAppealSummary()}
    >
      <Icon name={IconName.download} size="M" color={theme.color.link.linkDefault01} />
      <span className={classes.downloadPdfText}>Download Summary</span>
    </Link>
  );

interface ResitSummaryParams {
  registrationUuid?: string;
}

const AppealSummary = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();

  const location = useLocation<ResitSummaryParams>();
  const { uuid } = parseLocationSearch(location.search);
  const [fileStatus, setFileStatus] = useState(FileUploadState.IDLE);

  const { data: getAppealSummary } = candidateService.appeal.candidate.useGetAppealReceiptSummary(
    uuid
  );

  const {
    refetch: downloadAppealSummary,
    isFetching: isFetchingDownloadAppealSummary,
  } = candidateService.appeal.candidate.useCandidateDownloadSummary(
    (download) => {
      const newWindow = window.open(download?.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    (_) => {
      setFileStatus(FileUploadState.FAILED);
    },
    uuid || ''
  );

  const handleCloseDownloadErrorPrompt = () => {
    setFileStatus(FileUploadState.IDLE);
  };

  return (
    <>
      {getAppealSummary ? (
        <>
          <Grid container>
            <Grid xs={12}>
              <SectionHeader
                title={t('appeal->>AppealSummary.ApplicationDetails')}
              >
                {SectionHeaderAdditionalComponent(
                    downloadAppealSummary,
                    isFetchingDownloadAppealSummary,
                    theme,
                    classes
                )}
              </SectionHeader>
            </Grid>
            <Grid xs={12}>
              <ApplicationStatus {...getAppealSummary.applicationStatus} />
            </Grid>
          </Grid>

          <Divider />

          <AppealOutcome appealOutcome={getAppealSummary.applicationOutcome} />

          <Divider />

          <Grid container>
            <Grid xs={12}>
              <PersonalDetails {...getAppealSummary.candidateDetails} />
            </Grid>
          </Grid>

          <Divider />

          <Grid container>
            <Grid xs={12}>
              <SelectedSubjectsReview {...getAppealSummary.selectedSubjects} />
            </Grid>
          </Grid>

          <Divider />

          <Grid container>
            <Grid xs={12}>
              <FeeDetails {...getAppealSummary.appealFees} />
            </Grid>
          </Grid>

          <Divider />

          <Grid container>
            <Grid xs={12}>
              <TransactionDetails {...getAppealSummary.transactionDetails} />
            </Grid>
          </Grid>

          <Grid container>
            <Grid xs={12} lg={4}>
              <Button
                id="button-appealSummary-goToAppealApplications"
                className={classes.button}
                onClick={() => history.push(`/${appeal}/${applications.route}`)}
                variant="outlined"
              >
                {t('appeal->>AppealReceipt.BackToAppealApplications')}
              </Button>
            </Grid>
          </Grid>
          {fileStatus === FileUploadState.FAILED && (
            <DownloadProgressModal
              modalState
              fileDownloadStatus={FileUploadState.FAILED}
              onDialogClose={handleCloseDownloadErrorPrompt}
            />
          )}
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default AppealSummary;
